import { Controller } from '@hotwired/stimulus'
import $ from 'jquery';
import select2 from 'select2'
// Events

// $(this.select).on('select2:select', this.onChange)
// $(this.select).on('select2:unselect', this.onUnselect)
// $(this.select).on('select2:closing', this.onClosing)
// $(this.select).on('select2:close', this.onClose)
// $(this.select).on('select2:opening', this.onOpening)
// $(this.select).on('select2:open', this.onOpen)
// $(this.select).on('select2:selecting', this.onSelecting)
// $(this.select).on('select2:select', this.onSelect)
// $(this.select).on('select2:unselecting', this.onSelecting)
// $(this.select).on('select2:clearing', this.onClearing)
// $(this.select).on('select2:clear', this.onClear)

export default class extends Controller {
  static values = {
    url: String,
    forceHtmlSelected: Boolean,
    autoCloseDropdown: Boolean,
    preventClickBleed: { type: Boolean, default: false }
  }

  get select() {
    return $(this.element);
  }

  get options() {
    let data = $(this.select).data();
    let options = {
      minimumResultsForSearch: 1
    };

    for (let item in data) {
      if (item.indexOf('select2') > -1) {
        options = { ...options, [this.parseSelect2OptionName(item)]: data[item] };
      }
    }

    // Opt-in (data-result-limit="N"): only show the first N matching options so
    // a long local list (e.g. account names) stays scannable.
    const resultLimit = parseInt(this.element.dataset.resultLimit, 10);
    if (!Number.isNaN(resultLimit)) {
      let lastTerm = null;
      let shown = 0;
      // Reset the counter whenever the dropdown reopens — without this, `shown`
      // stays pinned at the limit from the previous pass when the term hasn't
      // changed (e.g. open -> close -> open with no typing), rendering an empty
      // dropdown. Wired to select2:opening in connect().
      this.resetResultLimitMatcher = () => { lastTerm = null; shown = 0; };
      options.matcher = (params, item) => {
        const term = (params.term || '').trim().toLowerCase();
        if (term !== lastTerm) { lastTerm = term; shown = 0; }
        if (item.children) { return item; }
        if (term && (item.text || '').toLowerCase().indexOf(term) === -1) { return null; }
        if (shown >= resultLimit) { return null; }
        shown += 1;
        return item;
      };
    }

    return options;
  }

  loadOptions() {
    fetch(this.urlValue, {
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const selectedValues = this.getSelectedValuesFromURL()

        data.forEach(record => {
          // Check if this is an optgroup (has children) or a regular option
          if (record.children) {
            // This is an optgroup
            const optgroup = $('<optgroup>').attr('label', record.text)
            record.children.forEach(child => {
              const isSelected = selectedValues.includes(child.id.toString())
              if (!isSelected) {
                const option = new Option(child.text, child.id, false, false)
                optgroup.append(option)
              }
            })
            this.select.append(optgroup)
          } else if (record.id && record.text) {
            // This is a regular option (object format)
            const isSelected = selectedValues.includes(record.id.toString())
            if (!isSelected) {
              const option = new Option(record.text, record.id, false, false)
              this.select.append(option)
            }
          } else {
            // Legacy array format [text, id]
            const isSelected = selectedValues.includes(record[1])
            if (!isSelected) {
              let option = new Option(record[0], record[1], false, false);
              this.select.append(option);
            }
          }
        });
        this.select.trigger('change');
      })
      .catch(error => console.error('Error loading options:', error));
  }

  // Previously returned the entire query string as a single string, causing substring
  // matches across all URL params - e.g., a user ID in team_manager_ids[] would be
  // excluded from other select2 fields on the page.
  getSelectedValuesFromURL() {
    const params = new URLSearchParams(window.location.search)
    const fieldName = this.element.name
    return params.getAll(fieldName)
  }

  connect() {
    this.handleEscape = this.handleEscape.bind(this)

    // Build options exactly once: the getter creates fresh closures (e.g. the
    // result-limit matcher), so calling it repeatedly would desync them.
    const options = this.options;
    $(this.select).select2(options);

    $(this.select).on('select2:select', this.onChange)
    $(this.select).on('select2:unselect', this.onUnselect)
    $(this.select).on('select2:select', () => { this.handleDropdown() })

    // Add escape key handler when dropdown opens
    $(this.select).on('select2:open', () => {
      document.addEventListener('keydown', this.handleEscape, true)
    })

    // Remove escape key handler when dropdown closes
    $(this.select).on('select2:close', () => {
      document.removeEventListener('keydown', this.handleEscape, true)
    })

    // Restart the result-limit matcher's counter on every open (see options getter)
    $(this.select).on('select2:opening', () => {
      if (this.resetResultLimitMatcher) { this.resetResultLimitMatcher() }
    })

    if(options.openOnFocus) {
      $(this.select).select2('open');
    }

    if (this.hasUrlValue) {
      this.loadOptions();
    }

    if (this.forceHtmlSelectedValue) {
      this.applyHtmlSelectedFix(this.select[0]);
    }

    if (this.preventClickBleedValue) {
      this.setupClickBleedPrevention()
    }
  }

  // Prevents click event bleeding from the clear button (X) to the select element underneath,
  // which would immediately reopen the dropdown and potentially auto-select the first option (
  // or the option under the pointer).
  //
  // This is not enabled by default because the tradeoff is changing the default control behavior.
  // Also, the bug only affects certain pages for unclear reasons.
  setupClickBleedPrevention() {
    let justCleared = false

    $(this.select).on('select2:clearing', () => {
      justCleared = true
      // Reset flag after a brief moment
      setTimeout(() => justCleared = false, 100)
    })

    $(this.select).on('select2:opening', (e) => {
      if (justCleared) {
        e.preventDefault() // Stop the dropdown from opening
        return false
      }
    })
  }

  // Sledgehammer for some Turbo+select2 issue where a manual page reload (not "hard" refresh)
  // will sometimes result in a <select> that has 1 or more selected options that is not
  // initialized properly resulting in an empty Select2 input but has selected options.
  //
  // To detect this we check if the option has the 'selected' HTML attribute but
  // has a 'selected' _property_ equal to false.
  // Lastly, we trigger a change event to ensure select2 re-initializes the selections.
  //
  // The setTimeout is a speculative fix so the DOM processing happens after the DOM
  // has "settled".
  applyHtmlSelectedFix(selectElement) {
      if (!selectElement)
          return;

      setTimeout(() => {
          let allValues = [...selectElement.options];

          let forced = 0
          allValues.forEach(option => {
              if (option.hasAttribute("selected") && !option.selected) {
                  option.selected = true; // Force-apply the selection
                  forced += 1;
              }
          });

          let selectedValues = allValues
              .filter(option => option.selected && option.value !== "")
              .map(option => option.value);

          if (selectedValues.length > 0 && forced > 0) {
            $(selectElement).val(selectedValues).trigger("change.select2");
          }
      }, 25);
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleEscape, true)
    if ($(this.select).data('select2')) {
      $(this.select).select2('destroy');
    }
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      // Check if select2 dropdown is open
      if ($(this.select).data('select2') && $(this.select).data('select2').isOpen()) {
        // Stop propagation to prevent closing parent offcanvas/modal
        event.stopPropagation()
        // Let select2 handle closing the dropdown
      }
    }
  }

  onChange(e) {
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleDropdown() {
    if (this.autoCloseDropdownValue === true) {
      this.closeDropdown();
    }
  }

  onUnselect(e) {
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }

  parseSelect2OptionName = (item) => {
    item = item.slice('select2'.length);
    return item.charAt(0).toLowerCase() + item.slice(1);
  };

  closeDropdown() {
    const dropdownMenu = this.element.closest('.dropdown-menu');
    if (dropdownMenu) {
      const toggle = dropdownMenu.previousElementSibling;
      if (toggle && toggle.classList.contains('dropdown-toggle')) {
        const bsDropdown = bootstrap.Dropdown.getInstance(toggle);
        if (bsDropdown) {
          bsDropdown.hide();
        }
      }
    }
  }
}
